/**
 * Snap-to-grid utilities for timeline (Task 4.6)
 *
 * Provides functions to snap time values to grid intervals,
 * ensuring tools align with visual grid lines and maintaining
 * precise timing.
 *
 * Grid interval: 0.1 seconds (matching track grid lines)
 */

/**
 * Default grid interval in seconds
 * Matches the visual grid lines in Track component
 */
export const GRID_INTERVAL = 0.1;

/**
 * Snap a time value to the nearest grid interval
 *
 * Rounds the time to the nearest multiple of the grid interval
 * and clamps to a minimum of 0 (no negative times).
 *
 * @param time - Time value in seconds to snap
 * @param gridInterval - Grid interval in seconds (default: 0.1s)
 * @returns Snapped time value, minimum 0
 *
 * @example
 * ```typescript
 * snapToGrid(1.14);  // Returns 1.1
 * snapToGrid(1.16);  // Returns 1.2
 * snapToGrid(-0.5);  // Returns 0
 * ```
 */
export function snapToGrid(time: number, gridInterval = GRID_INTERVAL): number {
  // Round to nearest grid interval
  const snapped = Math.round(time / gridInterval) * gridInterval;

  // Clamp to minimum 0 (no negative times)
  return Math.max(0, snapped);
}

/**
 * Convert time to pixel position on timeline
 *
 * @param time - Time in seconds
 * @param pixelsPerSecond - Pixels per second scale (default: 100)
 * @returns Pixel position
 */
export function timeToPixels(time: number, pixelsPerSecond = 100): number {
  return time * pixelsPerSecond;
}

/**
 * Convert pixel position to time on timeline
 *
 * @param pixels - Pixel position
 * @param pixelsPerSecond - Pixels per second scale (default: 100)
 * @returns Time in seconds
 */
export function pixelsToTime(pixels: number, pixelsPerSecond = 100): number {
  return pixels / pixelsPerSecond;
}

/**
 * Snap pixel position to grid and convert to time
 *
 * Convenience function that combines pixelsToTime and snapToGrid.
 * Useful for drag & drop operations where mouse position needs
 * to be converted to snapped time.
 *
 * @param pixels - Pixel position
 * @param pixelsPerSecond - Pixels per second scale (default: 100)
 * @param gridInterval - Grid interval in seconds (default: 0.1s)
 * @returns Snapped time in seconds
 *
 * @example
 * ```typescript
 * snapPixelsToGrid(514, 100);  // Returns 5.1 (514px → 5.14s → snap to 5.1s)
 * ```
 */
export function snapPixelsToGrid(
  pixels: number,
  pixelsPerSecond = 100,
  gridInterval = GRID_INTERVAL
): number {
  const time = pixelsToTime(pixels, pixelsPerSecond);
  return snapToGrid(time, gridInterval);
}
